// Sell-price computation — THE single source of truth for cost→price math.
//
// `costCents` is stored per PURCHASE unit (the "BUY" column, purchaseUom). Sell
// prices and job-estimate line prices are expressed per CONSUMPTION unit (the
// "SELL" column, consumptionUom). `conversionFactor` = how many consumptionUom
// units one purchaseUom unit yields (e.g. coil bought by the LB, sold by the LF,
// factor 4.22 → 1 LB makes 4.22 LF). So the price per consumption unit divides the
// cost down BEFORE applying markup:
//
//   costPerConsumptionCents = costCents / conversionFactor
//   sellPriceCents          = round(costPerConsumptionCents * (1 + markup/100))   // round ONCE
//
// WHOLE-UNIT PURCHASE EXCEPTION (insulation): some SKUs are sold in a continuous
// unit (SF) but can only be BOUGHT in an indivisible whole unit (BAG). For these,
// the caller has ALREADY converted the quantity into whole bags (see bagsFromSf in
// insulation.ts), `costCents` is the per-BAG cost, and the price is per BAG — so we
// must NOT divide by the factor. `isWholePurchaseUnit(sku)` is that signal, keyed on
// `coverageSf > 0` so it is byte-identical to the route's SF→bags conversion gate
// (routes/quotes.ts, routes/pricing.ts). Keeping ONE predicate is load-bearing: if
// the engine's "don't divide" gate were broader than the route's "do convert" gate,
// a line could be neither converted nor divided → a costPerBag×SF overcharge.
//
// All money is INTEGER CENTS. Never use floats for money. Round ONCE, at the final
// sell-price step (not mid-calculation).

/** Inputs for one SKU's per-consumption-unit sell price. */
export interface ComputeSellPriceInput {
  /** Vendor cost in cents, per PURCHASE unit (purchaseUom / "BUY"). */
  costCents: number;
  /** Consumption units per one purchase unit. null/0/≤0/non-finite → treated as 1. */
  conversionFactor: number | null | undefined;
  /**
   * True ⇒ the purchase unit is indivisible at sale (insulation BAG→SF) and the
   * quantity is already in purchase units; do NOT divide cost by the factor.
   * Callers MUST derive this from `isWholePurchaseUnit(sku)` so every site agrees.
   */
  isWholeUnitPurchase: boolean;
}

export interface ComputeSellPriceResult {
  /** Final sell price per consumption unit, in whole cents (rounded ONCE). */
  sellPriceCents: number;
  /**
   * Cost expressed per consumption unit, UN-rounded (float). Use this — never raw
   * costCents — as the cost basis for margin display, so margin stays
   * ≈ markup/(1+markup) regardless of the conversion factor.
   */
  costPerConsumptionCents: number;
  /**
   * True when `conversionFactor` was null/0/≤0/non-finite and therefore treated as
   * 1 (no conversion). For a SKU whose purchaseUom ≠ consumptionUom this means the
   * price is NOT actually converted — surface it as a data-quality warning.
   */
  factorFlagged: boolean;
}

/**
 * Compute the sell price per consumption unit for one SKU.
 *
 * Assumes `markupPct` is a finite, non-negative number (the engine validates and
 * throws BEFORE calling this; client callers default the category markup to 0).
 * This function NEVER throws — a thrown error here would white-screen the inventory
 * grid. Output is finite by construction (effectiveFactor ≥ 1 always).
 */
export function computeSellPriceCents(
  input: ComputeSellPriceInput,
  markupPct: number,
): ComputeSellPriceResult {
  const cf = input.conversionFactor;
  const factorFlagged = !(typeof cf === 'number' && Number.isFinite(cf) && cf > 0);

  // Whole-unit purchase (insulation) and missing/invalid factors both price at the
  // raw per-purchase-unit cost (effectiveFactor = 1, no division).
  const effectiveFactor = input.isWholeUnitPurchase || factorFlagged ? 1 : (cf as number);

  const costPerConsumptionCents = input.costCents / effectiveFactor;
  const safeMarkup = Number.isFinite(markupPct) && markupPct >= 0 ? markupPct : 0;

  // Round ONCE, at the final step.
  const sellPriceCents = Math.round(costPerConsumptionCents * (1 + safeMarkup / 100));

  return { sellPriceCents, costPerConsumptionCents, factorFlagged };
}

/**
 * THE single whole-purchase-unit predicate. `coverageSf > 0` is the proxy for "the
 * purchase unit is indivisible at sale" (insulation: 1 BAG covering N SF). It is a
 * boolean dressed as a magnitude — for insulation `coverageSf === conversionFactor`
 * — so do NOT read SF-per-bag as semantically meaningful in the price decision; it
 * is only ever asked "is this > 0".
 *
 * MUST stay byte-identical to the route's SF→bags conversion gate
 * (routes/quotes.ts, routes/pricing.ts) so the "don't divide cost" decision and the
 * "do convert quantity" decision can never desync.
 */
export function isWholePurchaseUnit(sku: { coverageSf?: number | null }): boolean {
  return sku.coverageSf != null && sku.coverageSf > 0;
}
